// NEW (not in TP, which has only running / done): the six job states as one 20px token each, so rows line up.
// Done is TP's `StepCheck`; running is TP's `Spinner sm` in a 20px box (TP's bare 16px spinner sat next to a 20px
// check); the other four are onboarding's, on the `ds-badge-*` tokens.
import { AlertCircle, Minus, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { StepCheck } from '@/components/ui/step-check';
import type { StepState } from '@/contracts/onboarding-session';
import { cn } from '@/lib/utils';

/** The words a screen reader gets after the label ("Read the site (done)"). The icon itself is decorative. */
export const STEP_STATUS_TEXT: Readonly<Record<StepState, string>> = {
  queued: 'queued',
  running: 'in progress',
  done: 'done',
  partial: 'partly done',
  unavailable: "couldn't read",
  skipped: 'skipped',
};

const TOKEN = 'flex size-5 shrink-0 items-center justify-center rounded-full';

/** The 20px round token for a state that is not clean: a warning (partial), an error (unavailable) or neutral (skipped). */
export function StatusToken({ tone, className, children }: { tone: 'warning' | 'error' | 'neutral'; className?: string; children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className={cn(
        TOKEN,
        tone === 'warning' && 'bg-ds-badge-warning-bg text-ds-badge-warning-text',
        tone === 'error' && 'bg-ds-badge-error-bg text-ds-badge-error-text',
        tone === 'neutral' && 'bg-ds-badge-neutral-bg text-ds-badge-neutral-text',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StepStatusIcon({ status }: { status: StepState }) {
  switch (status) {
    case 'done':
      return <StepCheck />;
    case 'running':
      return (
        <span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
          <Spinner size="sm" />
        </span>
      );
    case 'queued':
      return <span aria-hidden className="size-5 shrink-0 rounded-full border border-ds-hairline" />;
    case 'partial':
      return (
        <StatusToken tone="warning">
          <AlertCircle className="size-3.5" />
        </StatusToken>
      );
    case 'unavailable':
      return (
        <StatusToken tone="error">
          <X className="size-3.5" />
        </StatusToken>
      );
    case 'skipped':
      return (
        <StatusToken tone="neutral">
          <Minus className="size-3.5" />
        </StatusToken>
      );
  }
}
